import { Loader2, RefreshCw } from "lucide-react"
import { useEffect, useState, useSyncExternalStore } from "react"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { getShellNavPendingHref, subscribeShellNavPending } from "@/lib/shell-nav-pending"

/** After this long the spinner turns into a hard reload of the clicked page, in case the visit hung. */
const STALLED_AFTER_MS = 10_000

/** Covers the main column only. Idle renders nothing, so menu clicks do not touch the page tree. */
export function ShellMainPendingOverlay() {
  const pending = useSyncExternalStore(subscribeShellNavPending, getShellNavPendingHref, getShellNavPendingHref)
  if (!pending) return null

  // key: a click to another section restarts the stall timer.
  return <PendingOverlay key={pending} href={pending} />
}

function PendingOverlay({ href }: { href: string }) {
  const { t } = useTranslation("layout")
  const [stalled, setStalled] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setStalled(true), STALLED_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [])

  return (
    <div
      role="status"
      className="pointer-events-auto absolute inset-0 z-10 flex items-center justify-center bg-[var(--ui-overlay-light)]"
    >
      {stalled ? (
        <div className="flex flex-col items-center gap-3 px-6 text-center">
          <p className="text-sm text-ui-text-secondary">{t("shell.nav.stalled")}</p>
          <Button type="button" size="sm" variant="secondary" onClick={() => window.location.assign(href)}>
            <RefreshCw className="h-3.5 w-3.5" />
            {t("shell.nav.reload")}
          </Button>
        </div>
      ) : (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
          <span className="sr-only">{t("shell.nav.loading")}</span>
        </>
      )}
    </div>
  )
}
